import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, Spinner } from 'react-bootstrap';
import { useMarketingEmailSendStateBySendId } from '../hooks/useSyncState';

// Module-level map of claimId → send_id. Survives component unmount/remount
// so a MarketingEmailSendButton keeps following "its" send even if the parent
// re-renders it out and back (modal close/open, tab switch).
const buttonSendClaims: Map<string, string> = new Map();

interface MarketingEmailSendButtonProps {
  // Stable identity for this button across unmount/remount. Must be unique
  // per button (e.g. `email-send-${emailId}`).
  claimId: string;
  // onClick returns the send_id this click initiated, or undefined if no
  // send was started. The button subscribes to status events for that
  // specific send_id only.
  onClick: () => Promise<string | undefined>;
  // Fires when SendGrid accepts the campaign for delivery (terminal). Use
  // this to close any containing modal.
  onAccepted?: (singlesendID: string | undefined) => void;
  // Fires when the send fails before SendGrid acceptance (terminal). Use
  // this to surface the error in the page.
  onFailed?: (errMsg: string) => void;
  disabled?: boolean;
  variant?: string;
  size?: 'sm' | 'lg';
  buttonText?: string;
  icon?: string;
  sendingText?: string;
}

export const MarketingEmailSendButton: React.FC<MarketingEmailSendButtonProps> = ({
  claimId,
  onClick,
  onAccepted,
  onFailed,
  disabled = false,
  variant = 'success',
  size,
  buttonText = 'Send',
  icon = 'bi-send',
  sendingText = 'Sending...',
}) => {
  const [mySendId, setMySendId] = useState<string | undefined>(() => buttonSendClaims.get(claimId));
  const sendState = useMarketingEmailSendStateBySendId(mySendId);

  const isSending = !!(sendState && (sendState.status === 'started' || sendState.status === 'progress'));
  const isAccepted = !!(sendState && sendState.status === 'accepted');
  const hasError = !!(sendState && sendState.status === 'failed');

  const showTooltip = isSending || isAccepted || hasError;
  const buttonRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const messages = hasError
    ? [sendState?.error || 'Send failed']
    : isAccepted
    ? ['Accepted by SendGrid for delivery']
    : (sendState as any)?.messages || (sendState?.progress?.message ? [sendState.progress.message] : ['Sending...']);

  // Mirror SyncButton's tooltip positioning logic exactly.
  useEffect(() => {
    const updatePosition = () => {
      if (buttonRef.current && showTooltip) {
        const rect = buttonRef.current.getBoundingClientRect();
        const tooltipWidth = 400;
        const halfTooltip = tooltipWidth / 2;
        const centerX = rect.left + rect.width / 2 + window.scrollX;
        const viewportWidth = document.documentElement.clientWidth;
        const clampedLeft = Math.min(centerX, viewportWidth - halfTooltip - 8);
        setTooltipPosition({
          top: rect.top + window.scrollY,
          left: Math.max(halfTooltip + 8, clampedLeft),
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showTooltip]);

  const tooltipBgColor = hasError ? '#dc3545' : isAccepted ? '#198754' : '#000';

  const tooltip = showTooltip ? (
    <div
      style={{
        position: 'absolute',
        top: tooltipPosition.top - 8,
        left: tooltipPosition.left,
        transform: 'translate(-50%, -100%)',
        backgroundColor: tooltipBgColor,
        color: '#fff',
        padding: '6px 10px',
        borderRadius: '4px',
        fontSize: '12px',
        whiteSpace: 'pre',
        zIndex: 9999,
        pointerEvents: 'none',
        maxWidth: '400px',
        wordBreak: 'break-word' as const,
      }}
    >
      {messages.join('\n')}
      <div
        style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '4px solid transparent',
          borderRight: '4px solid transparent',
          borderTop: `4px solid ${tooltipBgColor}`,
        }}
      />
    </div>
  ) : null;

  // See SyncButton: guards against the cleanup effect racing the SSE round-trip.
  const hadSendStateRef = useRef(false);
  // Make sure terminal callbacks fire exactly once per send.
  const firedTerminalRef = useRef<string | undefined>(undefined);

  const handleClick = async () => {
    const sendId = await onClick();
    if (sendId) {
      hadSendStateRef.current = false;
      firedTerminalRef.current = undefined;
      buttonSendClaims.set(claimId, sendId);
      setMySendId(sendId);
    }
  };

  // Fire terminal callbacks when a final status surfaces; drop the claim on
  // the defined→undefined transition (after useMarketingEmailSendEvents
  // evicts the entry post-cleanup-delay).
  useEffect(() => {
    if (sendState) {
      hadSendStateRef.current = true;
      if (mySendId && firedTerminalRef.current !== mySendId) {
        if (sendState.status === 'accepted') {
          firedTerminalRef.current = mySendId;
          onAccepted?.(sendState.sendgrid_singlesend_id);
        } else if (sendState.status === 'failed') {
          firedTerminalRef.current = mySendId;
          onFailed?.(sendState.error || 'unknown error');
        }
      }
      return;
    }
    if (mySendId && hadSendStateRef.current) {
      buttonSendClaims.delete(claimId);
      setMySendId(undefined);
      hadSendStateRef.current = false;
    }
  }, [mySendId, sendState, claimId, onAccepted, onFailed]);

  return (
    <div ref={buttonRef} style={{ display: 'inline-block' }}>
      <Button
        variant={hasError ? 'outline-danger' : variant}
        size={size}
        onClick={handleClick}
        disabled={disabled || isSending}
      >
        {isSending ? (
          <>
            <Spinner animation="border" size="sm" className="me-1" />
            {sendingText}
          </>
        ) : hasError ? (
          <>
            <i className="bi bi-exclamation-triangle me-1"></i>
            Send Error
          </>
        ) : (
          <>
            <i className={`bi ${icon} me-1`}></i>
            {buttonText}
          </>
        )}
      </Button>
      {tooltip && createPortal(tooltip, document.body)}
    </div>
  );
};
